import { FeatureCollection, MultiLineString, Point } from 'geojson'
import { useEffect, useMemo, useState } from 'react'
import { Layer, Source, useMap } from 'react-map-gl'
import { useSelector } from 'react-redux'
import { selectHighlightedFeatureIdInList, selectMissionTempVisualSettings } from 'store/selectors/mission'
import turf from 'turf'
import { IRecordedRoute, IRoute } from 'utils/interfaces'

const getGeometryPointCount = (geometry: any): number => {
  if (!geometry || !geometry.coordinates) return 0
  const type = (geometry as any).type
  if (type === 'LineString') {
    return geometry.coordinates.length
  }
  if (type === 'MultiLineString') {
    return geometry.coordinates.reduce((sum: number, line: any[]) => sum + (Array.isArray(line) ? line.length : 0), 0)
  }
  return 0
}

const mergeMultiLineString = (geometry: any): any => {
  if (!geometry || !geometry.coordinates || !geometry.coordinates.length) return geometry
  if (geometry.type !== 'MultiLineString') return geometry

  const lines: any[][] = geometry.coordinates
  if (lines.length <= 1) return geometry

  const mergedLines: any[][] = []
  let currentLine: any[] = [...lines[0]]

  for (let i = 1; i < lines.length; i++) {
    const nextLine = lines[i]
    if (!nextLine || !nextLine.length) continue

    const lastPt = currentLine[currentLine.length - 1]
    const firstPt = nextLine[0]

    if (lastPt && firstPt && Math.abs(lastPt[0] - firstPt[0]) < 1e-8 && Math.abs(lastPt[1] - firstPt[1]) < 1e-8) {
      currentLine.push(...nextLine.slice(1))
    } else {
      mergedLines.push(currentLine)
      currentLine = [...nextLine]
    }
  }
  if (currentLine.length) {
    mergedLines.push(currentLine)
  }

  return {
    ...geometry,
    type: 'MultiLineString',
    coordinates: mergedLines,
  }
}

const simplifyFeatureIfDense = (feature: any, threshold = 50): any => {
  if (!feature || !feature.geometry) return feature

  let geom = feature.geometry
  if (geom.type === 'MultiLineString') {
    geom = mergeMultiLineString(geom)
  }

  const featureToProcess = {
    ...feature,
    geometry: geom,
  }

  const pointCount = getGeometryPointCount(geom)
  if (pointCount > threshold) {
    try {
      const simplified = turf.simplify(featureToProcess, 0.00005, false)
      return simplified || featureToProcess
    } catch {
      return featureToProcess
    }
  }
  return featureToProcess
}

const CleaningRoutesOnMap = ({
  cleaningRoutes,
  hasSelection: hasSelectionProp = false,
  editingRoute,
  highlightedName,
  beforeId = 'robot-image-style',
}: {
  cleaningRoutes: (IRoute | IRecordedRoute)[]
  hasSelection?: boolean
  editingRoute?: IRoute | IRecordedRoute
  highlightedName?: string
  beforeId?: string
}) => {
  const mapContext = useMap()
  const map = mapContext?.current
  const safeBeforeId = beforeId && map && map.getLayer(beforeId) ? beforeId : undefined

  const [asyncBufferData, setAsyncBufferData] = useState<FeatureCollection>({
    type: 'FeatureCollection',
    features: [],
  })

  const highlightedFeatureIdInList = useSelector(selectHighlightedFeatureIdInList)

  const isRouteHighlighted = (r: any, isEditing: boolean): boolean => {
    if (isEditing) return true
    if (r.properties?.highlighted) return true
    if (highlightedName && r.properties?.name === highlightedName) return true
    if (highlightedFeatureIdInList != null) {
      if (r.id === highlightedFeatureIdInList) return true
      if (r.properties?.zone_id === highlightedFeatureIdInList) return true
      if (r.properties?.id_on_map === highlightedFeatureIdInList) return true
      if (r.properties?.name === highlightedFeatureIdInList) return true
    }
    return false
  }

  const effectiveHasSelection = hasSelectionProp || highlightedFeatureIdInList != null || !!highlightedName

  const backwardRoutesFeatureCollections: FeatureCollection<MultiLineString> = useMemo(() => {
    const features: any[] = []

    const addRoute = (route: any, isEditing: boolean) => {
      if ('properties' in route && route.properties?.backwardGeometry) {
        features.push({
          type: 'Feature',
          geometry: mergeMultiLineString(route.properties.backwardGeometry),
          properties: {
            ...route.properties,
            highlighted: isRouteHighlighted(route, isEditing),
          },
        })
      }
    }
    cleaningRoutes.forEach(route => addRoute(route, false))
    if (editingRoute) {
      addRoute(editingRoute, true)
    }
    return {
      type: 'FeatureCollection',
      features,
    }
  }, [cleaningRoutes, editingRoute, highlightedFeatureIdInList, highlightedName])

  /** Forward-only segments — used for forward line rendering on GPU */
  const forwardRoutesFeatureCollections: FeatureCollection<MultiLineString> = useMemo(() => {
    const features: any[] = []

    const addRoute = (route: any, isEditing: boolean) => {
      const hasForward = !!route.properties?.forwardGeometry?.coordinates?.length
      const hasBackward = !!route.properties?.backwardGeometry?.coordinates?.length

      if (hasForward) {
        features.push({
          type: 'Feature',
          geometry: mergeMultiLineString(route.properties.forwardGeometry),
          properties: {
            ...route.properties,
            highlighted: isRouteHighlighted(route, isEditing),
          },
        })
      } else if (!hasBackward) {
        if (route.geometry?.coordinates?.length) {
          features.push({
            ...route,
            geometry: mergeMultiLineString(route.geometry),
            properties: {
              ...route.properties,
              highlighted: isRouteHighlighted(route, isEditing),
            },
          })
        }
      }
    }
    cleaningRoutes.forEach(route => addRoute(route, false))
    if (editingRoute) {
      addRoute(editingRoute, true)
    }
    return {
      type: 'FeatureCollection',
      features,
    }
  }, [cleaningRoutes, editingRoute, highlightedFeatureIdInList, highlightedName])

  /** Forward arrows — rendered for forward segments */
  const forwardRoutesArrowFeatureCollections: FeatureCollection<MultiLineString> = useMemo(() => {
    const features: any[] = []

    const addRoute = (route: any, isEditing: boolean) => {
      const hasForward = !!route.properties?.forwardGeometry?.coordinates?.length
      const hasBackward = !!route.properties?.backwardGeometry?.coordinates?.length

      if (hasForward) {
        features.push({
          type: 'Feature',
          geometry: mergeMultiLineString(route.properties.forwardGeometry),
          properties: {
            ...route.properties,
            highlighted: isRouteHighlighted(route, isEditing),
          },
        })
      } else if (!hasBackward) {
        if (route.geometry?.coordinates?.length) {
          features.push({
            ...route,
            geometry: mergeMultiLineString(route.geometry),
            properties: {
              ...route.properties,
              highlighted: isRouteHighlighted(route, isEditing),
            },
          })
        }
      }
    }

    cleaningRoutes.forEach(r => addRoute(r, false))
    if (editingRoute) {
      addRoute(editingRoute, true)
    }

    return {
      type: 'FeatureCollection',
      features,
    }
  }, [cleaningRoutes, editingRoute, highlightedFeatureIdInList, highlightedName])

  /** Backward arrows — rendered for routes with backward geometry */
  const backwardRoutesArrowFeatureCollections: FeatureCollection<MultiLineString> = useMemo(() => {
    const features: any[] = []

    const addRoute = (route: any, isEditing: boolean) => {
      if (route.properties?.backwardGeometry?.coordinates?.length) {
        features.push({
          type: 'Feature',
          geometry: mergeMultiLineString(route.properties.backwardGeometry),
          properties: {
            ...route.properties,
            highlighted: isRouteHighlighted(route, isEditing),
          },
        })
      }
    }

    cleaningRoutes.forEach(r => addRoute(r, false))
    if (editingRoute) {
      addRoute(editingRoute, true)
    }

    return {
      type: 'FeatureCollection',
      features,
    }
  }, [cleaningRoutes, editingRoute, highlightedFeatureIdInList, highlightedName])

  const backwardRoutePointsFeatureCollections: FeatureCollection<Point> = useMemo(() => {
    const features: any[] = []
    if (editingRoute && 'properties' in editingRoute && (editingRoute.properties as any).backwardGeometry) {
      let geom = (editingRoute.properties as any).backwardGeometry as MultiLineString
      if (geom.coordinates) {
        geom.coordinates.forEach(line => {
          line.forEach(coord => {
            features.push({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: coord },
              properties: { ...editingRoute.properties },
            })
          })
        })
      }
    }
    return {
      type: 'FeatureCollection',
      features,
    }
  }, [editingRoute])

  /** Calculate buffer asynchronously in background to ensure 0 ms UI click latency and 100% exact route fill */
  useEffect(() => {
    const routesToBuffer = effectiveHasSelection
      ? cleaningRoutes.filter(r => isRouteHighlighted(r, r.id === editingRoute?.id))
      : cleaningRoutes.concat(editingRoute ? [editingRoute] : [])

    if (routesToBuffer.length === 0) {
      setAsyncBufferData({ type: 'FeatureCollection', features: [] })
      return
    }

    const timer = setTimeout(() => {
      try {
        const features = routesToBuffer
          .map(route => {
            const hasForward = !!route.properties?.forwardGeometry?.coordinates?.length
            const hasBackward = !!route.properties?.backwardGeometry?.coordinates?.length

            let rawGeometry: any = null
            if (hasForward) {
              rawGeometry = route.properties.forwardGeometry
            } else if (!hasBackward) {
              rawGeometry = route.geometry
            }

            if (!rawGeometry || !rawGeometry.coordinates?.length) return null

            const exactGeometry = mergeMultiLineString(rawGeometry)
            const featureToBuffer: any = {
              type: 'Feature',
              geometry: exactGeometry,
              properties: route.properties,
            }

            const buffered = turf.buffer(featureToBuffer as any, 0.6, 'meters')
            if (buffered) {
              buffered.properties = { ...route.properties }
            }
            return buffered as any
          })
          .filter(Boolean)

        setAsyncBufferData({
          type: 'FeatureCollection',
          features,
        })
      } catch (e) {
        console.warn('[CleaningRoutesOnMap] Async buffer calculation error:', e)
      }
    }, 10)

    return () => clearTimeout(timer)
  }, [cleaningRoutes, editingRoute, highlightedName, effectiveHasSelection, highlightedFeatureIdInList])

  const visualSettings = useSelector(selectMissionTempVisualSettings)

  const isHighlightedExpr: any = useMemo(
    () =>
      highlightedName
        ? ['any', ['==', ['get', 'highlighted'], true], ['==', ['get', 'name'], highlightedName]]
        : ['==', ['get', 'highlighted'], true],
    [highlightedName]
  )

  const cleaningRouteBufferStyle: any = useMemo(() => {
    return {
      id: 'gl-draw-cleaning-routes-buffer',
      type: 'fill',
      paint: {
        'fill-color': ['coalesce', ['get', 'color'], visualSettings.cleaning_route_color],
        'fill-opacity': effectiveHasSelection ? ['case', isHighlightedExpr, 0.18, 0.08] : 0.15,
      },
    }
  }, [visualSettings, effectiveHasSelection, isHighlightedExpr])

  const cleaningRouteStyle = useMemo<any>(() => {
    return {
      id: 'gl-draw-cleaning-routes',
      type: 'line',
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': ['coalesce', ['get', 'color'], visualSettings.cleaning_route_color],
        'line-opacity': effectiveHasSelection
          ? ['case', isHighlightedExpr, 1.0, 0.5]
          : visualSettings.cleaning_route_opacity,
        'line-width': ['case', isHighlightedExpr, 4, visualSettings.cleaning_route_width],
      },
    }
  }, [visualSettings, effectiveHasSelection, isHighlightedExpr])

  const backwardRouteStyle = useMemo<any>(() => {
    return {
      id: 'gl-draw-cleaning-routes-backward',
      type: 'line',
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#D06A4C',
        'line-opacity': effectiveHasSelection ? ['case', isHighlightedExpr, 1.0, 0.5] : 1.0,
        'line-width': ['case', isHighlightedExpr, 4, visualSettings.cleaning_route_width],
      },
    }
  }, [visualSettings, effectiveHasSelection, isHighlightedExpr])

  /** Forward arrows (filled triangle) */
  const cleaningRouteArrowsStyle = useMemo<any>(() => {
    return {
      id: 'gl-draw-cleaning-routes-arrows',
      type: 'symbol',
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 50,
        'text-field': '\u25B6',
        'text-size': ['interpolate', ['linear'], ['zoom'], 14, 12, 20, 18],
        'text-keep-upright': false,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#E6F5F4',
        'text-halo-color': ['coalesce', ['get', 'color'], visualSettings.cleaning_route_color],
        'text-halo-width': 2,
        'text-opacity': effectiveHasSelection ? ['case', isHighlightedExpr, 1.0, 0.5] : 1.0,
      },
    }
  }, [visualSettings, effectiveHasSelection, isHighlightedExpr])

  /** Backward arrows */
  const backwardRouteArrowsStyle = useMemo<any>(() => {
    return {
      id: 'gl-draw-cleaning-routes-backward-arrows',
      type: 'symbol',
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 50,
        'text-field': '\u25C0',
        'text-size': ['interpolate', ['linear'], ['zoom'], 14, 12, 20, 18],
        'text-keep-upright': false,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#E6F5F4',
        'text-halo-color': '#D06A4C',
        'text-halo-width': 2,
        'text-opacity': effectiveHasSelection ? ['case', isHighlightedExpr, 1.0, 0.5] : 1.0,
      },
    }
  }, [effectiveHasSelection, isHighlightedExpr])

  const backwardRoutePointHaloStyle = useMemo<any>(() => {
    return {
      id: 'gl-draw-cleaning-routes-backward-points-halo',
      type: 'circle',
      paint: {
        'circle-radius': 9,
        'circle-color': '#D06A4C',
      },
    }
  }, [])

  const backwardRoutePointInnerStyle = useMemo<any>(() => {
    return {
      id: 'gl-draw-cleaning-routes-backward-points-inner',
      type: 'circle',
      paint: {
        'circle-radius': 6,
        'circle-color': 'rgba(255, 255, 255, 0.5)',
      },
    }
  }, [])

  return (
    <>
      <Source id="cleaning-routes-buffer" type="geojson" data={asyncBufferData}>
        <Layer {...cleaningRouteBufferStyle} beforeId={safeBeforeId} />
      </Source>
      {/* Full route line — always shown on GPU for fast rendering */}
      <Source id="cleaning-routes" type="geojson" data={forwardRoutesFeatureCollections} tolerance={1}>
        <Layer {...cleaningRouteStyle} beforeId={safeBeforeId} />
      </Source>
      {/* Forward-only arrows — rendered only on highlighted route */}
      {forwardRoutesArrowFeatureCollections.features.length > 0 && (
        <Source id="cleaning-routes-forward" type="geojson" data={forwardRoutesArrowFeatureCollections} tolerance={1}>
          <Layer {...cleaningRouteArrowsStyle} beforeId={safeBeforeId} />
        </Source>
      )}
      {/* Backward segments & arrows */}
      <Source id="cleaning-routes-backward" type="geojson" data={backwardRoutesFeatureCollections} tolerance={1}>
        <Layer {...backwardRouteStyle} beforeId={safeBeforeId} />
      </Source>
      {backwardRoutesArrowFeatureCollections.features.length > 0 && (
        <Source
          id="cleaning-routes-backward-arrows"
          type="geojson"
          data={backwardRoutesArrowFeatureCollections}
          tolerance={1}
        >
          <Layer {...backwardRouteArrowsStyle} beforeId={safeBeforeId} />
        </Source>
      )}
      {backwardRoutePointsFeatureCollections.features.length > 0 && (
        <Source id="cleaning-routes-backward-points" type="geojson" data={backwardRoutePointsFeatureCollections}>
          <Layer {...backwardRoutePointHaloStyle} beforeId={safeBeforeId} />
          <Layer {...backwardRoutePointInnerStyle} beforeId={safeBeforeId} />
        </Source>
      )}
    </>
  )
}

export default CleaningRoutesOnMap
